import { useState, useRef, useEffect } from 'react';
import { Upload, Loader2, Camera } from 'lucide-react';
import { uploadAPI, fetchMediaAsBlob } from '../../services/api';
import { markFilePickerActive } from '../../context/PasscodeContext';
import { checkFileUploadPermissions } from '../../services/native';

/**
 * Profile picture upload component - uploads directly on file select
 * No base64 conversion - uses multipart form data
 */
export default function ProfilePictureUpload({
  value, // Current profile picture URL (proxy URL like /api/media/... or null)
  onUpload, // Called with { url, publicId } after successful upload
  onRemove, // Called when removing the picture
  fallbackImage = null, // Fallback image URL (e.g., Google avatar)
  userName = '', // User name for initials fallback
  label = 'Upload Photo',
  compact = false,
  className = ''
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [fallbackError, setFallbackError] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [blobLoading, setBlobLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Extract URL from value (can be string or object with url property)
  const imageUrl = typeof value === 'string' ? value : value?.url || null;

  // Load proxy image as blob for display
  const isProxyUrl = imageUrl && imageUrl.startsWith('/api/media');

  // Load blob URL for proxy images
  useEffect(() => {
    let url = null;

    const loadProxyImage = async () => {
      if (isProxyUrl) {
        setBlobLoading(true);
        try {
          url = await fetchMediaAsBlob(imageUrl);
          setBlobUrl(url);
          setImgError(false);
        } catch (err) {
          console.error('Failed to load profile image:', err);
          setImgError(true);
        } finally {
          setBlobLoading(false);
        }
      } else {
        setBlobUrl(null);
      }
    };

    loadProxyImage();

    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [imageUrl, isProxyUrl]);

  // Reset image error when value changes
  const prevValueRef = useRef(value);
  if (prevValueRef.current !== value) {
    prevValueRef.current = value;
    if (imgError) setImgError(false);
  }

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input for re-selection
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Only image files are allowed');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be less than 2MB');
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      // Upload directly to server
      const result = await uploadAPI.profilePicture(file);

      // Call onUpload with the result
      if (onUpload) {
        onUpload(result);
      }
    } catch (err) {
      console.error('Profile picture upload error:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove();
    }
  };

  const openFilePicker = async () => {
    if (isUploading) return;

    // Check permissions on native before opening picker
    const hasPermission = await checkFileUploadPermissions();
    if (!hasPermission) return;

    // Mark file picker as active to skip passcode lock when returning from gallery/files
    markFilePickerActive();
    fileInputRef.current?.click();
  };

  // Get initials from user name
  const initial = userName?.charAt(0)?.toUpperCase() || 'U';

  // Determine image source
  const effectiveImageSrc = isProxyUrl ? blobUrl : imageUrl;
  const hasValidImage = effectiveImageSrc && !imgError;
  const hasValidFallback = fallbackImage && !fallbackError;
  const showImage = hasValidImage || hasValidFallback;
  const imageSrc = hasValidImage ? effectiveImageSrc : fallbackImage;

  // Render avatar content
  const renderAvatarContent = () => {
    if (isUploading || blobLoading) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-surface-100">
          <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
        </div>
      );
    }

    if (showImage) {
      return (
        <>
          <img
            src={imageSrc}
            alt="Profile"
            className="w-full h-full object-cover"
            onError={() => {
              if (hasValidImage) {
                setImgError(true);
              } else {
                setFallbackError(true);
              }
            }}
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Upload className="w-5 h-5 text-white" />
          </div>
        </>
      );
    }

    // Fallback - show initials
    if (userName) {
      return (
        <>
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ backgroundColor: 'var(--accent-bg)' }}
          >
            <span className="text-white font-semibold text-2xl">{initial}</span>
          </div>
          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Upload className="w-5 h-5 text-white" />
          </div>
        </>
      );
    }

    // No image, no fallback, no userName - show upload icon
    return (
      <div className="absolute inset-0 flex items-center justify-center text-surface-400 group-hover:text-primary-500 transition-colors">
        <Upload className="w-6 h-6" />
      </div>
    );
  };

  // Compact mode — just avatar with camera icon overlay
  if (compact) {
    return (
      <div className={`relative flex-shrink-0 ${className}`}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
        <div
          onClick={openFilePicker}
          className={`relative w-14 h-14 rounded-full overflow-hidden bg-surface-100 cursor-pointer group ${
            isUploading ? 'pointer-events-none' : ''
          }`}
          style={!showImage && !isUploading && !blobLoading ? { backgroundColor: 'var(--accent-bg)' } : {}}
        >
          {(isUploading || blobLoading) ? (
            <div className="absolute inset-0 flex items-center justify-center bg-surface-100">
              <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
            </div>
          ) : showImage ? (
            <img
              src={imageSrc}
              alt="Profile"
              className="w-full h-full object-cover"
              onError={() => hasValidImage ? setImgError(true) : setFallbackError(true)}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-bold text-xl">{initial}</span>
            </div>
          )}
        </div>
        {/* Camera icon overlay */}
        <button
          type="button"
          onClick={openFilePicker}
          className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-white border-2 border-surface-100 flex items-center justify-center shadow-sm"
        >
          <Camera className="w-3 h-3 text-surface-500" />
        </button>
        {error && <p className="text-[10px] text-red-500 mt-1 absolute -bottom-4 left-0 whitespace-nowrap">{error}</p>}
      </div>
    );
  }

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      <div className="flex items-center gap-4">
        {/* Avatar Preview */}
        <div
          onClick={openFilePicker}
          className={`relative w-20 h-20 rounded-full overflow-hidden bg-surface-100 border-2 border-surface-200 cursor-pointer group ${
            isUploading ? 'pointer-events-none' : ''
          }`}
        >
          {renderAvatarContent()}
        </div>

        <div className="flex-1">
          <p className="text-sm font-medium text-surface-700">{label}</p>
          <p className="text-xs text-surface-500 mt-0.5">
            JPG, PNG (max 2MB)
          </p>
          {(value || fallbackImage) && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={isUploading}
              className="text-xs text-red-500 hover:text-red-600 mt-1 disabled:opacity-50"
            >
              Remove
            </button>
          )}
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-500 mt-2">{error}</p>
      )}
    </div>
  );
}
